import React, { useState, useEffect } from 'react';
import { submitTheologicalSurvey } from '../../api/onboardingService';

interface TheologyStepProps {
  data?: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface TheologyData {
  // Scripture section
  scriptureAffirmation: string;
  
  // God & Trinity section
  trinityUnderstanding: string;
  
  // Humanity & Sin section
  originalSinUnderstanding: string;
  
  // Christology section
  christologyUnderstanding: string;
  
  // Salvation section
  salvationDoctrines: string;
  gospelPresentation: string;
  
  // Ecclesiology section
  churchNatureMission: string;
  churchOrdinances: string;
  churchMembership: string;
  churchOffices: string;
  
  // Eschatology section
  eschatologicalConvictions: string;
  eschatologyMinistryImpact: string;
  
  // Baptist Distinctives section
  believersBaptism: string;
  churchAutonomy: string;
  
  // Ministry Philosophy & Practice section
  preachingPhilosophy: string;
  discipleMaking: string;
  worshipPhilosophy: string;
  evangelismSocialEngagement: string;
  theologicalDisagreement: string;
  
  // Missional & Cultural Engagement section
  gospelPluralisticCulture: string;
  contextualizationView: string;
  plantingChallenges: string;
  
  // Personal & Family Life section
  personalHoliness: string;
  marriageFamilyProtection: string;
  conflictHandling: string;
}

const TheologyStep: React.FC<TheologyStepProps> = ({ data, onComplete, onNext, onPrevious }) => {
  const [formData, setFormData] = useState<TheologyData>({
    scriptureAffirmation: '',
    trinityUnderstanding: '',
    originalSinUnderstanding: '',
    christologyUnderstanding: '',
    salvationDoctrines: '',
    gospelPresentation: '',
    churchNatureMission: '',
    churchOrdinances: '',
    churchMembership: '',
    churchOffices: '',
    eschatologicalConvictions: '',
    eschatologyMinistryImpact: '',
    believersBaptism: '',
    churchAutonomy: '',
    preachingPhilosophy: '',
    discipleMaking: '',
    worshipPhilosophy: '',
    evangelismSocialEngagement: '',
    theologicalDisagreement: '',
    gospelPluralisticCulture: '',
    contextualizationView: '',
    plantingChallenges: '',
    personalHoliness: '',
    marriageFamilyProtection: '',
    conflictHandling: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const sections = [
    {
      title: 'Scripture',
      fields: ['scriptureAffirmation'],
      description: 'Your understanding of the inspiration, authority, inerrancy, and sufficiency of Scripture'
    },
    {
      title: 'God & the Trinity',
      fields: ['trinityUnderstanding'],
      description: 'Your understanding of the Trinity'
    },
    {
      title: 'Humanity & Sin',
      fields: ['originalSinUnderstanding'],
      description: 'Your understanding of original sin and man\'s depravity'
    },
    {
      title: 'Christology',
      fields: ['christologyUnderstanding'],
      description: 'Your understanding of Jesus Christ\'s divinity, humanity, and work of atonement'
    },
    {
      title: 'Salvation',
      fields: ['salvationDoctrines', 'gospelPresentation'],
      description: 'Your understanding of justification, sanctification, perseverance, and gospel presentation'
    },
    {
      title: 'Ecclesiology',
      fields: ['churchNatureMission', 'churchOrdinances', 'churchMembership', 'churchOffices'],
      description: 'Your understanding of the nature, mission, ordinances, membership, and offices of the church'
    },
    {
      title: 'Eschatology',
      fields: ['eschatologicalConvictions', 'eschatologyMinistryImpact'],
      description: 'Your eschatological convictions and how they shape your ministry'
    },
    {
      title: 'Baptist Distinctives',
      fields: ['believersBaptism', 'churchAutonomy'],
      description: 'Your views on believer\'s baptism and church autonomy'
    },
    {
      title: 'Ministry Philosophy & Practice',
      fields: ['preachingPhilosophy', 'discipleMaking', 'worshipPhilosophy', 'evangelismSocialEngagement', 'theologicalDisagreement'],
      description: 'Your philosophy of preaching, disciple-making, worship, and handling theological disagreement'
    },
    {
      title: 'Missional & Cultural Engagement',
      fields: ['gospelPluralisticCulture', 'contextualizationView', 'plantingChallenges'],
      description: 'How you articulate the gospel in a pluralistic culture and approach contextualization'
    },
    {
      title: 'Personal & Family Life',
      fields: ['personalHoliness', 'marriageFamilyProtection', 'conflictHandling'],
      description: 'Your approach to personal holiness, family protection, and conflict handling'
    }
  ];

  useEffect(() => {
    if (data) {
      setFormData(prevData => ({ ...prevData, ...data }));
    }
  }, [data]);

  // Listen for navigation events from sidebar and update parent
  useEffect(() => {
    const handleNavigateToSection = (event: CustomEvent) => {
      const sectionIndex = event.detail;
      if (sectionIndex >= 0 && sectionIndex < sections.length) {
        setCurrentSection(sectionIndex);
        // Notify parent component to update sidebar highlight
        const updateEvent = new CustomEvent('subsectionChanged', { detail: sectionIndex });
        window.dispatchEvent(updateEvent);
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('navigateToSection' as any, handleNavigateToSection as EventListener);
    return () => {
      window.removeEventListener('navigateToSection' as any, handleNavigateToSection as EventListener);
    };
  }, []);

  // Check if a section is complete (all fields have content)
  const isSectionComplete = (sectionIndex: number): boolean => {
    const section = sections[sectionIndex];
    if (!section) return false;
    
    return section.fields.every(field => {
      const value = formData[field as keyof TheologyData];
      return value && value.trim().length > 0;
    });
  };

  // Calculate completion status for all sections
  const getSectionCompletionStatus = (): boolean[] => {
    return sections.map((_, index) => isSectionComplete(index));
  };

  // Notify parent when section changes internally (via next/prev buttons)
  useEffect(() => {
    const updateEvent = new CustomEvent('subsectionChanged', { detail: currentSection });
    window.dispatchEvent(updateEvent);
  }, [currentSection]);

  // Notify parent of completion status whenever formData changes
  useEffect(() => {
    const completionStatus = getSectionCompletionStatus();
    const updateEvent = new CustomEvent('subsectionCompletionChanged', { 
      detail: { completions: completionStatus }
    });
    window.dispatchEvent(updateEvent);
  }, [formData]);

  const validateCurrentSection = () => {
    const currentSectionData = sections[currentSection];
    const newErrors: Record<string, string> = {};

    currentSectionData.fields.forEach(field => {
      if (!formData[field as keyof TheologyData]?.trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleNextSection = async () => {
    if (validateCurrentSection()) {
      // Save current progress to database
      setIsSaving(true);
      setSaveError(null);
      setSaveSuccess(false);
      
      try {
        await submitTheologicalSurvey(formData);
        setSaveSuccess(true);
        
        // Brief delay to show success message
        setTimeout(() => {
          if (currentSection < sections.length - 1) {
            setCurrentSection(currentSection + 1);
            setSaveSuccess(false);
          } else {
            handleSubmit();
          }
        }, 800);
      } catch (error) {
        console.error('Error saving theology section:', error);
        setSaveError('Failed to save your progress. Please try again.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handlePreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    } else {
      onPrevious();
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    try {
      await onComplete(formData);
    } catch (error) {
      console.error('Error saving theology data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      scriptureAffirmation: 'Do you affirm the inspiration, authority, inerrancy, and sufficiency of Scripture? Explain.',
      trinityUnderstanding: 'Describe your understanding of the Trinity.',
      originalSinUnderstanding: 'What is your understanding of original sin and man\'s depravity?',
      christologyUnderstanding: 'Who is Jesus Christ? Explain His divinity, humanity, and work of atonement.',
      salvationDoctrines: 'How do you articulate the doctrines of justification, sanctification, and perseverance?',
      gospelPresentation: 'How do you present the gospel in a clear and biblical way?',
      churchNatureMission: 'Define the nature and mission of the church.',
      churchOrdinances: 'What are the two ordinances of the church, and how should they be practiced?',
      churchMembership: 'What is your view of church membership and discipline?',
      churchOffices: 'What is your understanding of the offices of pastor/elder and deacon?',
      eschatologicalConvictions: 'Briefly summarize your eschatological convictions.',
      eschatologyMinistryImpact: 'How do they shape your ministry?',
      believersBaptism: 'What is your view of believer\'s baptism by immersion?',
      churchAutonomy: 'How do you view the autonomy of the local church and cooperation with other churches (associations, state conventions, SBC)?',
      preachingPhilosophy: 'Describe your philosophy of preaching (expository, topical, doctrinal, etc.).',
      discipleMaking: 'How do you approach disciple-making and leadership development?',
      worshipPhilosophy: 'What is your philosophy of worship in the gathered church?',
      evangelismSocialEngagement: 'How do you understand the relationship between evangelism and social engagement?',
      theologicalDisagreement: 'How would you handle theological disagreement within your church?',
      gospelPluralisticCulture: 'How do you articulate the gospel in a pluralistic, secular culture?',
      contextualizationView: 'What is your view of contextualization in church planting?',
      plantingChallenges: 'What challenges do you foresee in planting within your target community?',
      personalHoliness: 'How do you pursue personal holiness and spiritual disciplines?',
      marriageFamilyProtection: 'How do you guard your marriage and family in ministry?',
      conflictHandling: 'How do you handle conflict, criticism, and discouragement in ministry?',
    };
    return labels[field] || field;
  };

  const currentSectionData = sections[currentSection];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Theological Survey</h2>
        <p className="text-gray-600">
          Please provide thoughtful responses to these theological questions. Your answers will help us understand your doctrinal convictions and ministry philosophy.
        </p>
        <div className="mt-4 p-4 bg-blue-50 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Reference:</strong> Please review the Baptist Faith & Message 2000 statement of faith at{' '}
                <a href="https://bfm.sbc.net/wp-content/uploads/2024/08/BFM2000.pdf" target="_blank" rel="noopener noreferrer" className="underline">
                  https://bfm.sbc.net/wp-content/uploads/2024/08/BFM2000.pdf
                </a>
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4 p-4 bg-green-50 rounded-md border border-green-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Auto-save enabled:</strong> Your progress will be saved automatically after each section. You can safely come back and continue later.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-gray-900">
            {currentSectionData.title}
          </h3>
          <span className="text-sm text-gray-500">
            Section {currentSection + 1} of {sections.length}
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {currentSectionData.description}
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Section Content */}
      <div className="space-y-6">
        {currentSectionData.fields.map((field) => (
          <div key={field}>
            <label htmlFor={field} className="block text-sm font-medium text-gray-700 mb-2">
              {getFieldLabel(field)}
            </label>
            <textarea
              id={field}
              name={field}
              rows={6}
              value={formData[field as keyof TheologyData]}
              onChange={handleInputChange}
              className={`block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                errors[field] ? 'border-red-300' : ''
              }`}
              placeholder="Please provide a thoughtful response..."
            />
            {errors[field] && <p className="mt-1 text-sm text-red-600">{errors[field]}</p>}
          </div>
        ))}
      </div>

      {/* Save Success Message */}
      {saveSuccess && (
        <div className="mt-6 p-4 bg-green-50 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">Progress saved successfully!</p>
            </div>
          </div>
        </div>
      )}

      {/* Save Error Message */}
      {saveError && (
        <div className="mt-6 p-4 bg-red-50 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200 mt-8">
        <button
          type="button"
          onClick={handlePreviousSection}
          disabled={isSaving}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {currentSection === 0 ? 'Previous Step' : 'Previous Section'}
        </button>

        <button
          type="button"
          onClick={handleNextSection}
          disabled={isLoading || isSaving}
          className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {(isLoading || isSaving) ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isSaving ? 'Saving...' : 'Loading...'}
            </>
          ) : currentSection === sections.length - 1 ? (
            'Complete Survey'
          ) : (
            <>
              Save & Continue
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TheologyStep;
